/**
 * @fileoverview Contact list state management hook
 * @description Управление списками контактов: CRUD операции (list metadata only).
 *
 * Data fetching powered by TanStack Query.
 * Local state retained for form/UI/mutation concerns.
 *
 * Note: Lead management (view leads, remove lead from list) is handled
 * directly in ContactListDetailsView via its own useQuery/useMutation.
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import { useContactListsQuery, useContactListDetailQuery } from '../hooks/outreach/queries';
import {
    createContactList as apiCreateContactList,
    updateContactList as apiUpdateContactList,
    deleteContactList as apiDeleteContactList
} from '../api/outreach/contactLists';
import type { ContactList, ContactListFormData, ContactListDetails } from '../types/outreach';

/** Опции для инициализации хука */
interface UseContactListStateOptions {
    onCampaignRefresh?: () => void;
}

const createEmptyContactListForm = (): ContactListFormData => ({
    name: '',
    description: '',
    source: ''
});

/**
 * Contact list state management hook.
 * TanStack Query for data fetching; local state for form/UI.
 */
export const useContactListState = ({
    onCampaignRefresh
}: UseContactListStateOptions = {}) => {
    const navigate = useNavigate();
    const qc = useQueryClient();

    // =========================================================================
    // FILTERS (local state — drive TQ query key)
    // =========================================================================
    const [contactListOwnerFilter, setContactListOwnerFilter] = useState<'my' | 'team' | 'all'>('all');
    const [contactListTeamFilter, setContactListTeamFilter] = useState<string | null>(null);
    const [contactListManagerFilter, setContactListManagerFilter] = useState<string | null>(null);

    const filters = useMemo(
        () => ({ owner: contactListOwnerFilter, teamId: contactListTeamFilter, managerId: contactListManagerFilter }),
        [contactListOwnerFilter, contactListTeamFilter, contactListManagerFilter]
    );

    // =========================================================================
    // TQ QUERIES
    // =========================================================================
    const [selectedListId, setSelectedListId] = useState<string | null>(null);

    const listsQuery = useContactListsQuery(filters);
    const detailQuery = useContactListDetailQuery(selectedListId);

    // Derived from TQ
    // Cast to typed shape — JS query returns untyped data; canonical envelope is
    // { items: ContactList[], userRole: string, teams: ..., managers: ... }.
    const _listsData = listsQuery.data as any;
    const contactLists: ContactList[] = _listsData?.items || [];
    const contactListsLoading = listsQuery.isLoading;
    const contactListUserRole: string = _listsData?.userRole || 'MANAGER';
    const contactListTeams: { id: string; name: string }[] = _listsData?.teams || [];
    const contactListManagers: { id: string; name: string; teamId?: string }[] = _listsData?.managers || [];
    const listDetails: ContactListDetails | null = (detailQuery.data as any)?.list ?? null;
    const listDetailsLoading = detailQuery.isLoading;

    // =========================================================================
    // LOCAL UI / FORM STATE
    // =========================================================================
    const [contactListsError, setContactListsError] = useState<string | null>(null);
    const [contactListForm, setContactListForm] = useState<ContactListFormData>(createEmptyContactListForm);
    const [contactListSaving, setContactListSaving] = useState(false);
    const [isCreatingContactList, setIsCreatingContactList] = useState(false);
    const [isContactListEditing, setIsContactListEditing] = useState(false);
    const [isContactListModalOpen, setIsContactListModalOpen] = useState(false);

    // =========================================================================
    // INVALIDATION HELPERS
    // =========================================================================
    const invalidateLists = useCallback(
        () => qc.invalidateQueries({ queryKey: queryKeys.outreach.contactLists.all }),
        [qc]
    );

    const invalidateDetail = useCallback(
        (id?: string | null) => qc.invalidateQueries({
            queryKey: queryKeys.outreach.contactLists.detail(id || selectedListId)
        }),
        [qc, selectedListId]
    );

    // =========================================================================
    // FORM SYNC from TQ detail data
    // =========================================================================
    useEffect(() => {
        const list = (detailQuery.data as any)?.list;
        if (!list) return;
        setContactListForm({
            name: list.name || '',
            description: list.description || '',
            source: list.source || ''
        });
    }, [detailQuery.data]);

    // =========================================================================
    // FACADE: fetchContactLists / fetchListDetails (OutreachContext compat)
    // =========================================================================
    const fetchContactLists = useCallback(async (): Promise<ContactList[]> => {
        await invalidateLists();
        const cached = qc.getQueryData<{ items?: ContactList[] }>(
            queryKeys.outreach.contactLists.list(filters)
        );
        return cached?.items || [];
    }, [invalidateLists, qc, filters]);

    const fetchListDetails = useCallback(async (
        listId: string
    ): Promise<ContactListDetails | null> => {
        if (!listId) return null;
        await qc.invalidateQueries({
            queryKey: queryKeys.outreach.contactLists.detail(listId)
        });
        const cached = qc.getQueryData<any>(
            queryKeys.outreach.contactLists.detail(listId)
        );
        return cached?.list || null;
    }, [qc]);

    // =========================================================================
    // HANDLERS
    // =========================================================================
    const startCreateContactList = useCallback(() => {
        setIsCreatingContactList(true);
        setIsContactListEditing(true);
        navigate('/outreach/lists/new');
        setSelectedListId(null);
        setContactListForm(createEmptyContactListForm());
        setContactListsError(null);
    }, [navigate]);

    const handleContactListEdit = useCallback(async (list?: ContactList) => {
        const target = list || (selectedListId ? contactLists.find(item => item.id === selectedListId) : null);
        if (!target) return;
        setIsCreatingContactList(false);
        setIsContactListEditing(true);
        navigate(`/outreach/lists/${target.id}`);
        setSelectedListId(target.id);
        setContactListsError(null);
        setContactListForm({
            name: target.name || '',
            description: target.description || '',
            source: target.source || ''
        });
    }, [contactLists, selectedListId, navigate]);

    const handleContactListFieldChange = useCallback(<K extends keyof ContactListFormData>(
        field: K,
        value: ContactListFormData[K]
    ) => {
        setContactListForm(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleSaveContactList = useCallback(async () => {
        const trimmedName = contactListForm.name.trim();
        if (!trimmedName) {
            setContactListsError('Введите название списка');
            return;
        }
        setContactListSaving(true);
        setContactListsError(null);
        try {
            const payload = {
                name: trimmedName,
                description: contactListForm.description?.trim() || null,
                source: contactListForm.source?.trim() || null
            };
            if (isCreatingContactList) {
                const data = await apiCreateContactList(payload) as { list?: ContactList };
                await invalidateLists();
                const cached = qc.getQueryData<{ items?: ContactList[] }>(
                    queryKeys.outreach.contactLists.list(filters)
                );
                const items = cached?.items || [];
                const newId = data?.list?.id || items[0]?.id || null;
                if (newId) {
                    setIsCreatingContactList(false);
                    setIsContactListEditing(true);
                    setSelectedListId(newId);
                    navigate(`/outreach/lists/${newId}`);
                }
            } else if (selectedListId) {
                await apiUpdateContactList(selectedListId, payload);
                await Promise.all([invalidateLists(), invalidateDetail()]);
                setIsContactListEditing(true);
            }
        } catch (err) {
            console.error('Failed to save contact list', err);
            const apiError = err as { data?: { error?: string }; message?: string };
            setContactListsError(apiError?.data?.error || apiError.message || 'Unknown error');
        } finally {
            setContactListSaving(false);
        }
    }, [contactListForm, isCreatingContactList, selectedListId, invalidateLists, invalidateDetail, qc, filters, navigate]);

    const handleDeleteContactList = useCallback(async (listId?: string) => {
        const targetId = listId || selectedListId;
        if (!targetId) return;
        if (!window.confirm('Удалить список контактов?')) return;

        setContactListSaving(true);
        setContactListsError(null);
        try {
            await apiDeleteContactList(targetId);
            await invalidateLists();
            const cached = qc.getQueryData<{ items?: ContactList[] }>(
                queryKeys.outreach.contactLists.list(filters)
            );
            const items = cached?.items || [];
            if (items.length === 0) {
                setContactListForm(createEmptyContactListForm());
            } else {
                setIsCreatingContactList(false);
                setIsContactListEditing(false);
                navigate('/outreach/lists');
            }
            setSelectedListId(null);
        } catch (err) {
            console.error('Failed to delete contact list', err);
            const apiError = err as { data?: { error?: string }; message?: string };
            setContactListsError(apiError?.data?.error || apiError.message || 'Unknown error');
        } finally {
            setContactListSaving(false);
        }
    }, [selectedListId, invalidateLists, navigate, qc, filters]);

    const handleCancelContactListEdit = useCallback(() => {
        setContactListsError(null);
        navigate('/outreach/lists');
        if (isCreatingContactList) {
            setContactListForm(createEmptyContactListForm());
            setIsCreatingContactList(false);
            return;
        }
        setIsContactListEditing(false);
        if (selectedListId) {
            const list = contactLists.find(item => item.id === selectedListId);
            if (list) {
                setContactListForm({
                    name: list.name || '',
                    description: list.description || '',
                    source: list.source || ''
                });
            } else {
                setContactListForm(createEmptyContactListForm());
            }
        } else {
            setContactListForm(createEmptyContactListForm());
        }
    }, [contactLists, selectedListId, isCreatingContactList, navigate]);

    return {
        contactLists,
        contactListsLoading,
        contactListOwnerFilter,
        setContactListOwnerFilter,
        contactListUserRole,
        contactListTeams,
        contactListManagers,
        contactListTeamFilter,
        setContactListTeamFilter,
        contactListManagerFilter,
        setContactListManagerFilter,
        contactListsError,
        contactListForm,
        contactListSaving,
        isCreatingContactList,
        isContactListEditing,
        isContactListModalOpen,
        selectedListId,
        setSelectedListId,
        listDetails,
        listDetailsLoading,
        fetchContactLists,
        fetchListDetails,
        startCreateContactList,
        handleContactListEdit,
        handleContactListFieldChange,
        handleSaveContactList,
        handleDeleteContactList,
        handleCancelContactListEdit
    };
};

export default useContactListState;
